import { zodResolver } from '@hookform/resolvers/zod'
import type { ParseKeys } from 'i18next'
import { Lock, LogOut } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { Banner } from '@/components/feedback/Banner'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { LanguageSelect } from '@/components/ui/LanguageSelect'
import { Logo } from '@/components/ui/Logo'
import { useChangePassword } from '@/features/auth/api/useChangePassword'
import { useSession } from '@/features/auth/api/useSession'
import { useSignOut } from '@/features/auth/api/useSignOut'
import { DEFAULT_LANGUAGE, isLanguage, LANGUAGE_STORAGE_KEY, type Language } from '@/i18n/config'
import { ApiError } from '@/lib/api'
import { describeProblem, fieldErrorKey, fieldErrors } from '@/lib/problems'

/* The password change (QW-5): `/change-password`.
 *
 * Two readers arrive here. A FLAGGED account — one still holding a password an
 * administrator generated — is sent by RequireSession and can reach nothing
 * else: the server answers every other route 403 `password-change-required`
 * until this form succeeds. An unflagged account may come on purpose from the
 * profile page and may leave again (the Cancel link exists only for them).
 *
 * The page is mounted under RequireSession but BESIDE AppShell, so none of the
 * shell's reads, the notification stream or the chat socket run for an account
 * the server would refuse. It therefore carries its own frame: the brand mark,
 * one card, and the two controls a reader with no shell still needs — the
 * language switch and a way out. The switch is LOCAL, like the sign-in page's:
 * the stored preference is written by `PUT /session/language`, which a flagged
 * account cannot call, and AppShell's `useLanguage` takes over again at home.
 *
 * Where each refusal lands follows the system's rule — field errors beside the
 * field, record errors above the record:
 *   · the four field codes the server sends (`currentPassword: incorrect`,
 *     `newPassword: too-short | too-long | unchanged`) sit under their field;
 *   · the repeat is checked HERE and never sent — the server has no such field;
 *   · anything else — a concurrent admin reset (409), the sign-in throttle
 *     (429), the network — is a Banner above the form, in the catalogue's words.
 * The client enforces no length: the policy belongs to the server, and the hint
 * under the new password states it so the server's refusal is no surprise.
 *
 * On success `useChangePassword` has already seeded the session with the
 * unflagged user when the navigation below runs — see that hook for why the
 * order is the difference between landing home and a redirect loop.
 */

const schema = z
  .object({
    currentPassword: z.string().min(1, 'currentRequired'),
    newPassword: z.string().min(1, 'nextRequired'),
    repeat: z.string().min(1, 'repeatRequired'),
  })
  .refine((form) => form.repeat === form.newPassword, {
    path: ['repeat'],
    message: 'mismatch',
  })

type ChangeForm = z.infer<typeof schema>

/** The server's two fields — the only ones a 422 here can name. */
const SERVER_FIELDS = ['currentPassword', 'newPassword'] as const

/** Every code this form renders, client and server alike, to its sentence. */
const MESSAGE_KEYS: Record<string, ParseKeys> = {
  currentRequired: 'auth.changePassword.errors.currentRequired',
  nextRequired: 'auth.changePassword.errors.nextRequired',
  repeatRequired: 'auth.changePassword.errors.repeatRequired',
  mismatch: 'auth.changePassword.errors.mismatch',
  incorrect: 'auth.changePassword.errors.incorrect',
  'too-short': 'auth.changePassword.errors.tooShort',
  'too-long': 'auth.changePassword.errors.tooLong',
  unchanged: 'auth.changePassword.errors.unchanged',
}

export function ChangePasswordPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { data: user } = useSession()
  const change = useChangePassword()
  const signOut = useSignOut()

  const language: Language = isLanguage(i18n.resolvedLanguage)
    ? i18n.resolvedLanguage
    : DEFAULT_LANGUAGE

  // Nothing else on this page keeps <html lang> in step — AppShell's
  // useLanguage is not mounted here.
  useEffect(() => {
    document.documentElement.lang = language
  }, [language])

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ChangeForm>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { currentPassword: '', newPassword: '', repeat: '' },
  })

  const forced = user?.mustChangePassword ?? true

  /* Field codes go beside their field; what is left is the record's. A 422 that
     names neither of the two fields still deserves a sentence, so it falls to
     the Banner rather than to silence. */
  const serverFields = fieldErrors(change.error)
  const fieldRefusal = SERVER_FIELDS.some((field) => serverFields[field]?.length)
  const banner =
    change.isError && !fieldRefusal ? bannerFor(change.error) : null

  const onSubmit = handleSubmit(({ currentPassword, newPassword }) => {
    change.mutate(
      { currentPassword, newPassword },
      {
        onError: (error) => {
          const fields = fieldErrors(error)
          for (const field of SERVER_FIELDS) {
            const codes = fields[field]
            if (codes?.length) setError(field, { type: 'server', message: codes.join(' ') })
          }
        },
        // The session is already seeded (useChangePassword), so the guard lets
        // this through rather than sending the reader back.
        onSuccess: () => navigate('/', { replace: true }),
      },
    )
  })

  /** One field's message: every code it carries, each in the reader's words. */
  const message = (codes: string | undefined) =>
    codes
      ? codes
          .split(' ')
          .map((code) => t(MESSAGE_KEYS[code] ?? fieldErrorKey(code)))
          .join(' ')
      : undefined

  function bannerFor(error: unknown): { title: string; detail: string } {
    // The one 409 this route has: an admin reset landed while the change was in
    // flight. Matched on the status, not a detail token — the route has no
    // other conflict to tell it from.
    if (error instanceof ApiError && error.status === 409) {
      return {
        title: t('auth.changePassword.changedElsewhere.title'),
        detail: t('auth.changePassword.changedElsewhere.detail'),
      }
    }
    const { titleKey, detailKey } = describeProblem(error)
    return { title: t(titleKey), detail: t(detailKey) }
  }

  const pending = change.isPending

  return (
    <div className="bg-surface-page relative flex min-h-dvh flex-col items-center justify-center px-4 py-10">
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
        <LanguageSelect
          value={language}
          onChange={(next: Language) => {
            try {
              window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next)
            } catch {
              /* A private window may refuse storage; the switch still applies. */
            }
            void i18n.changeLanguage(next)
          }}
        />
      </div>

      <main className="flex w-full max-w-[440px] flex-col items-center gap-8">
        <Logo variant="mark-blue" height={40} />

        <Card className="w-full">
          <CardBody className="flex flex-col gap-5 p-6 sm:p-8">
            <div className="flex flex-col gap-1.5">
              <h1 className="text-text-heading text-[22px] leading-tight font-normal">
                {t('auth.changePassword.title')}
              </h1>
              <p className="text-text-muted text-[14px] leading-snug font-light">
                {forced
                  ? t('auth.changePassword.subtitleForced')
                  : t('auth.changePassword.subtitleVoluntary')}
              </p>
            </div>

            {banner ? (
              <div role="alert">
                <Banner tone="negative" title={banner.title}>
                  {banner.detail}
                </Banner>
              </div>
            ) : null}

            <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
              <Field
                label={t('auth.changePassword.current')}
                error={message(errors.currentPassword?.message)}
              >
                {(field) => (
                  <Input
                    {...register('currentPassword')}
                    {...field}
                    type="password"
                    autoComplete="current-password"
                    autoFocus
                    iconLeft={Lock}
                    disabled={pending}
                  />
                )}
              </Field>

              <Field
                label={t('auth.changePassword.next')}
                hint={t('auth.changePassword.policy')}
                error={message(errors.newPassword?.message)}
              >
                {(field) => (
                  <Input
                    {...register('newPassword')}
                    {...field}
                    type="password"
                    autoComplete="new-password"
                    iconLeft={Lock}
                    disabled={pending}
                  />
                )}
              </Field>

              <Field
                label={t('auth.changePassword.repeat')}
                error={message(errors.repeat?.message)}
              >
                {(field) => (
                  <Input
                    {...register('repeat')}
                    {...field}
                    type="password"
                    autoComplete="new-password"
                    iconLeft={Lock}
                    disabled={pending}
                  />
                )}
              </Field>

              <Button
                type="submit"
                size="lg"
                disabled={pending}
                aria-busy={pending}
                className="mt-1 w-full"
              >
                {pending ? t('auth.changePassword.pending') : t('auth.changePassword.submit')}
              </Button>
            </form>
          </CardBody>
        </Card>

        <div className="flex flex-wrap items-center justify-center gap-2">
          {/* A voluntary change can be abandoned; a forced one cannot — there
              is nowhere in the app a flagged account could go back to. */}
          {forced ? null : (
            <Link
              to="/profile"
              className="text-text-muted hover:text-text-heading rounded-md px-3 py-1.5 text-[13px] font-normal no-underline transition-colors duration-[var(--dur-fast)]"
            >
              {t('common.actions.cancel')}
            </Link>
          )}
          {/* The way out for a reader who cannot or will not choose a password
              now — a shared machine, the wrong account. Quiet, not danger:
              signing out destroys nothing. */}
          <Button
            variant="ghost"
            size="sm"
            iconLeft={LogOut}
            disabled={signOut.isPending}
            onClick={() =>
              signOut.mutate(undefined, {
                onSettled: () => navigate('/login', { replace: true }),
              })
            }
          >
            {t('auth.signOut')}
          </Button>
        </div>
      </main>
    </div>
  )
}
